import { styled } from "goober";
import { type ReactNode, useMemo } from "react";
import { connectorUsageSourceLabel } from "../../../shared/connectorUsageSources";
import { useUrlSort } from "../../hooks/useUrlSort";
import { makeUrl, routes } from "../../lib/urls";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { GhostPill } from "../GhostPill";
import { Icon, type IconName } from "../Icon";
import { type FilterDef, ListFilterPills } from "../ListFilter";
import { ListTable } from "../ListTable";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import { useMonitoringInteraction } from "./analytics";
import {
  type ConnectorSummary,
  formatErrorRate,
  type WorkflowSummary,
} from "./connectorTraffic";
import { NameBody, NameCell, ScrollArea, TableCard } from "./monitoringList";
import { Panel } from "./workflowsLayout";

type ConnectorMeta = { name: string; identifier: string };
type WorkflowInfo = {
  id: string;
  name: string;
  badgeSeed: string | null;
  pausedAt: number | null;
};

// ListTable mounts a DOM row per item, and a busy connector can be called by
// many workflows.
const ROW_LIMIT = 100;

// Non-workflow usage sources, shown below the ranked workflows in the drill-in.
const SOURCE_ICON: Record<string, IconName> = {
  personal_chat: "message-circle",
  connection_test: "plug",
  audit_log_delivery: "shield-check",
  git_sync: "git-branch",
  workflow_builder: "square-code",
  autofix: "sparkles",
  autotune: "activity",
  step_test: "play",
  other: "circle-dashed",
};

const FilterFooter = styled("div")`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  min-height: 40px;
  border-top: 0.5px solid var(--border-alpha);
`;

const GhostRowLogo = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--alpha8);
  flex-shrink: 0;
`;

// Sample rows for the empty state, matching the connector table's shape.
const GHOST_BREAKDOWN_ROWS = [
  { key: "g1", name: 132, requests: 40, errorRate: 30 },
  { key: "g2", name: 96, requests: 36, errorRate: 28 },
  { key: "g3", name: 152, requests: 44, errorRate: 32 },
  { key: "g4", name: 112, requests: 38, errorRate: 26 },
  { key: "g5", name: 124, requests: 42, errorRate: 30 },
  { key: "g6", name: 88, requests: 34, errorRate: 24 },
  { key: "g7", name: 140, requests: 46, errorRate: 28 },
];

function sortValue(
  row: { requests: number; errors: number },
  column: string | null
): number {
  switch (column) {
    case "errorRate":
      return row.requests === 0 ? -1 : row.errors / row.requests;
    default:
      return row.requests;
  }
}

export function ConnectorBreakdownPanel({
  connectorSummaries,
  workflowSummaries,
  connectorMeta,
  workflowsById,
  windowPhrase,
  selectedConnectorId,
  unattributedSources,
  onClearConnector,
  placeholder,
}: {
  connectorSummaries: readonly ConnectorSummary[];
  workflowSummaries: readonly WorkflowSummary[];
  connectorMeta: ReadonlyMap<string, ConnectorMeta>;
  workflowsById: ReadonlyMap<string, WorkflowInfo>;
  windowPhrase: string;
  selectedConnectorId: string | null;
  unattributedSources: readonly {
    source: string;
    requests: number;
    errors: number;
  }[];
  onClearConnector: () => void;
  placeholder?: boolean;
}) {
  const recordInteraction = useMonitoringInteraction();
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.monitoring.connectors,
    { requests: "desc", errorRate: "desc" }
  );
  const drilled = selectedConnectorId != null;

  const connectorRows = useMemo(() => {
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...connectorSummaries].sort(
      (a, b) =>
        dir * (sortValue(a, sortColumn) - sortValue(b, sortColumn)) ||
        a.connectorId.localeCompare(b.connectorId)
    );
  }, [connectorSummaries, sortColumn, sortDirection]);

  const workflowRows = useMemo(() => {
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...workflowSummaries]
      .sort(
        (a, b) =>
          dir * (sortValue(a, sortColumn) - sortValue(b, sortColumn)) ||
          a.workflowId.localeCompare(b.workflowId)
      )
      .slice(0, ROW_LIMIT);
  }, [workflowSummaries, sortColumn, sortDirection]);

  if (placeholder) {
    return (
      <Panel style={{ minHeight: 0 }}>
        <TableCard aria-hidden>
          <ScrollArea>
            <ListTable
              headers={{
                connector: "Connector",
                requests: "Requests",
                errorRate: "Error rate",
              }}
              columns={{
                connector: "flex",
                requests: "md",
                errorRate: "md",
              }}
              items={GHOST_BREAKDOWN_ROWS.map((row) => ({
                key: row.key,
                connector: (
                  <NameCell>
                    <GhostRowLogo />
                    <NameBody>
                      <GhostPill style={{ width: row.name }} />
                    </NameBody>
                  </NameCell>
                ),
                requests: <GhostPill style={{ width: row.requests }} />,
                errorRate: <GhostPill style={{ width: row.errorRate }} />,
              }))}
            />
          </ScrollArea>
        </TableCard>
      </Panel>
    );
  }

  const onSort = (column: string) => {
    recordInteraction(
      drilled ? "connectors.workflow_table_sort" : "connectors.table_sort",
      { value: column }
    );
    handleSort(column);
  };

  const selectedMeta = selectedConnectorId
    ? connectorMeta.get(selectedConnectorId)
    : undefined;
  const selectedName = selectedMeta?.name ?? "Deleted connector";

  const connectorFilters: FilterDef[] = selectedConnectorId
    ? [
        {
          kind: "single",
          key: "connector",
          label: "Connector",
          icon: null,
          options: [
            {
              id: selectedConnectorId,
              label: selectedName,
              pillLeft: (
                <ConnectorLogo
                  template={{
                    name: selectedName,
                    identifier: selectedMeta?.identifier ?? "unknown",
                  }}
                  size={12}
                />
              ),
            },
          ],
          value: selectedConnectorId,
          onSelect: () => {},
          onClear: onClearConnector,
        },
      ]
    : [];

  const logo = (connectorId: string): ReactNode => {
    const meta = connectorMeta.get(connectorId);
    return (
      <ConnectorLogoContainer
        size={32}
        borderRadius={8}
        outline={false}
        hoverBackground={false}
      >
        <ConnectorLogo
          template={{
            name: meta?.name ?? "Deleted connector",
            identifier: meta?.identifier ?? "unknown",
          }}
          size={16}
        />
      </ConnectorLogoContainer>
    );
  };

  const numericCells = (row: { requests: number; errors: number }) => ({
    requests: (
      <Text variant="text-small">{row.requests.toLocaleString("en-US")}</Text>
    ),
    errorRate: (
      <Text variant="text-small">
        {formatErrorRate(row.errors, row.requests)}
      </Text>
    ),
  });

  const workflowItems = workflowRows.map((row) => {
    const workflow = workflowsById.get(row.workflowId);
    return {
      key: `${row.workflowId}/${row.connectorId}`,
      link:
        workflow && row.spaceId
          ? makeUrl(routes.space.workflow, {
              spaceId: row.spaceId,
              workflowId: row.workflowId,
            })
          : undefined,
      workflow: (
        <NameCell>
          {workflow ? (
            <WorkflowBadge
              workflow={{
                id: workflow.id,
                badgeSeed: workflow.badgeSeed,
                pausedAt: workflow.pausedAt,
                // Anything with traffic in the window is live, so render the
                // full-color (non-blueprint) badge.
                branches: [{ name: "main" }],
              }}
              hover={false}
            />
          ) : (
            logo(row.connectorId)
          )}
          <NameBody>
            <Text variant="text-regular-md" ellipsis>
              {workflow?.name ?? "Deleted workflow"}
            </Text>
          </NameBody>
        </NameCell>
      ),
      ...numericCells(row),
    };
  });

  // Non-workflow usage, broken out by source and pinned below the workflows.
  for (const entry of unattributedSources) {
    workflowItems.push({
      key: `source:${entry.source}`,
      link: undefined,
      workflow: (
        <NameCell>
          <ConnectorLogoContainer
            size={32}
            borderRadius={8}
            outline={false}
            hoverBackground={false}
          >
            <Icon
              name={SOURCE_ICON[entry.source] ?? "circle-dashed"}
              size={16}
              color="var(--text-secondary)"
            />
          </ConnectorLogoContainer>
          <NameBody>
            <Text variant="text-regular-md" ellipsis>
              {connectorUsageSourceLabel(entry.source)}
            </Text>
          </NameBody>
        </NameCell>
      ),
      ...numericCells(entry),
    });
  }

  return (
    <Panel style={{ minHeight: 0 }}>
      <TableCard>
        <ScrollArea>
          {drilled ? (
            <ListTable
              headers={{
                workflow: "Workflow",
                requests: "Requests",
                errorRate: "Error rate",
              }}
              columns={{
                workflow: "flex",
                requests: "md",
                errorRate: "md",
              }}
              sortableColumns={sortableColumns}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
              emptyState={
                <Text variant="text-regular" color="var(--text-secondary)">
                  No workflow called {selectedName} {windowPhrase}.
                </Text>
              }
              items={workflowItems}
            />
          ) : (
            <ListTable
              headers={{
                connector: "Connector",
                requests: "Requests",
                errorRate: "Error rate",
              }}
              columns={{
                connector: "flex",
                requests: "md",
                errorRate: "md",
              }}
              sortableColumns={sortableColumns}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
              emptyState={
                <Text variant="text-regular" color="var(--text-secondary)">
                  No connector usage recorded {windowPhrase}.
                </Text>
              }
              items={connectorRows.map((row) => {
                const meta = connectorMeta.get(row.connectorId);
                const name = meta?.name ?? "Deleted connector";
                return {
                  key: row.connectorId,
                  link: meta
                    ? makeUrl(routes.modal.connector, {
                        id: row.connectorId,
                        tab: "usage",
                      })
                    : undefined,
                  connector: (
                    <NameCell>
                      {logo(row.connectorId)}
                      <NameBody>
                        <Text variant="text-regular-md" ellipsis>
                          {name}
                        </Text>
                      </NameBody>
                    </NameCell>
                  ),
                  ...numericCells(row),
                };
              })}
            />
          )}
        </ScrollArea>
        {drilled && (
          <FilterFooter>
            <ListFilterPills filters={connectorFilters} />
          </FilterFooter>
        )}
      </TableCard>
    </Panel>
  );
}
